import React from 'react';

import {
  CircularProgress,
  Divider,
  Typography,
} from '@material-ui/core';

import { KpiCompany } from 'generated/graphql';
import { observer } from 'mobx-react-lite';
import { Controller, FormProvider } from 'react-hook-form';

import { EeInput } from 'components/EeInput/EeInput';
import { EeMenu } from 'components/EeMenu/EeMenu';
import { EeModal } from 'components/EeModal/EeModal';
import { EeSelect } from 'components/EeSelect/EeSelect';
import { EeTable } from 'components/EeTable/EeTable';

import { useCompanyStatusPage } from './useCompanyStatusPage';

export const CompanyStatusPage = observer(() => {
  const logic = useCompanyStatusPage();

  return (
    <div className="bg-gray w-100 mx-auto px-3" style={{ maxWidth: 1200 }}>
      <Typography variant="h1" className="my-3">
        KPI Company Status
      </Typography>
      <div className="d-flex flex-wrap">
        <FormProvider {...logic.methods}>
          <Controller
            name="search"
            defaultValue=""
            rules={{ required: false }}
            render={(props) => (
              <EeInput
                {...props}
                search
                type="search"
                className="mr-3"
                notFullWidth
                placeholder="Search"
              />
            )}
          />
          <div className="d-flex align-items-center flex-grow-1 mr-3">
            <span className="mr-2">Filter by</span>
            <Controller
              name="fund"
              defaultValue={0}
              rules={{ required: false }}
              render={(props) => (
                <EeSelect
                  {...props}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                  }}
                  className="mr-2"
                  items={[
                    { id: 0, name: 'All Funds' },
                    ...logic.models.funds
                      .sort()
                      .map<{ id: string; name: string }>(
                        (name: React.ReactText) => ({
                          id: name.toString(),
                          name: name.toString(),
                        }),
                      ),
                  ]}
                />
              )}
            />
            <Controller
              name="sector"
              defaultValue={0}
              rules={{ required: false }}
              render={(props) => (
                <EeSelect
                  {...props}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                  }}
                  className="mr-2"
                  items={[
                    { id: 0, name: 'All Sectors' },
                    { id: 1, name: 'No Sector' },
                    ...logic.models.sectors
                      .sort()
                      .map<{ id: string; name: string }>((name: String) => ({
                        id: name.toString(),
                        name: name.toString(),
                      })),
                  ]}
                />
              )}
            />
            <Controller
              name="investment"
              defaultValue={0}
              rules={{ required: false }}
              render={(props) => (
                <EeSelect
                  {...props}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                  }}
                  items={[
                    { id: 0, name: 'All Investments' },
                    { id: 1, name: '≤ 1,000,000' },
                    { id: 2, name: '≥ 1,000,000' },
                  ]}
                />
              )}
            />
          </div>
        </FormProvider>
        <EeMenu
          label={
            logic.assignInProgress || logic.models.loading ? (
              <CircularProgress />
            ) : (
              'Assign Template'
            )
          }
          color='primary'
          disabled={logic.assignInProgress || logic.models.loading}
          variant='contained'
          items={[
            { id: '-1', name: 'Unassign Template' },
            { id: '', name: '' },
            ...logic.tmplModels.templates.map<{ id: string; name: string }>(
              ({ id, name }) => ({
                id: id?.toString() || '',
                name: name?.toString() || '',
              }),
            ),
          ]}
          onSelect={logic.confirmTemplate}
        />
      </div>
      <Divider />
      {logic.models.loading && <p>Loading...</p>}
      {logic.models.error ? (
        <p>ERROR: {logic.models.error.message}</p>
      ) : (
        <EeTable
          columns={logic.columns}
          selectable={true}
          data={logic.models.companies}
          onSelect={logic.setSelectedRows}
        />
      )}
      <EeModal
        open={logic.modal.open}
        onClose={logic.closeModal}
        title={logic.modal.title}
        submitTitle={logic.modal.submitTitle}
        onSubmit={logic.assignUnassignTemplate}
      >
        <div>
          <p>{logic.modal.text}</p>
          <ul>
            {logic.selectedRows.sort().map((row: KpiCompany, index: number) => (
              <li key={row.id || `item_${index.toString()}`}>
                {row.templateId && logic.modal.template.id !== '-1' ? '*' : ''}{' '}
                {row.name}
              </li>
            ))}
          </ul>
        </div>
      </EeModal>
    </div>
  );
});
